// Notifications: a full page (its own route, #n=<id>) summarising what needs a
// human, plus the push-enablement controls. Reached from the bell on every
// daemon-scoped page, so the same view is shared across the app.
import { el, clear } from "../dom.js";
import { Connection } from "../relay.js";
import { syncPush } from "../push.js";
import { renderPushControls } from "../pushcontrols.js";
import { bottomNav } from "../nav.js";
import { backButton } from "../bell.js";
import type { DaemonEntry } from "../store.js";
import type { Attention } from "../types.js";

// pendingCount is the single definition of "how many things need you" — the
// bell badge and this page both derive their number from it.
export function pendingCount(a: Attention): number {
  const plans = a.plans.filter((p) => p.status === "proposed").length;
  return a.decisions.length + plans + a.reviews.length + a.audits.length;
}

// notifyNewAttention mirrors the Web Push ping over the live socket: Web Push
// only fires when the app isn't running, so an open app would otherwise update
// silently. When new attention arrives while connected, raise an OS
// notification too — so a ping lands whether the app is open or not. The first
// fetch after connecting is the baseline (prev == null) and never pings, so
// already-pending items don't notify on open. Pings are keyed on the item ids
// that are new since the last fetch, so a swap (one item resolves as another
// arrives) still notifies even though the count is flat. Shares the push
// notification's "fabrika-attention" tag so a socket ping and a real push
// collapse into one rather than stacking.
export async function notifyNewAttention(
  daemonName: string,
  prev: Attention | null,
  next: Attention,
): Promise<void> {
  if (!prev) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  if (!("serviceWorker" in navigator)) return;
  const seen = new Set(pendingItems(prev).map((i) => i.id));
  const fresh = pendingItems(next).filter((i) => !seen.has(i.id));
  if (fresh.length === 0) return;
  const body = fresh.length === 1 ? fresh[0].label : `${fresh[0].label} · +${fresh.length - 1} more`;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification(daemonName || "Fabrika", {
      body,
      tag: "fabrika-attention",
      icon: "/app/icon-192.png",
      badge: "/app/icon-192.png",
    });
  } catch {
    // Best-effort: a not-yet-ready service worker must not break live updates.
  }
}

// pendingItems flattens an Attention into the things needing a human, each as a
// stable id (category-scoped, so a task that is both reviewed and audited stays
// distinct) plus a one-line label. Ordered like the feed: decisions, plans,
// reviews, audits — so the headline names the highest-priority new arrival.
interface PendingItem {
  id: string;
  label: string;
}

function pendingItems(a: Attention): PendingItem[] {
  const items: PendingItem[] = [];
  for (const d of a.decisions) {
    items.push({ id: `decision:${d.id}`, label: d.question || "A decision needs you" });
  }
  for (const p of a.plans.filter((pl) => pl.status === "proposed")) {
    items.push({ id: `plan:${p.id}`, label: `Approve plan: ${p.bigTask?.title || "untitled"}` });
  }
  for (const r of a.reviews) {
    items.push({ id: `review:${r.task.id}`, label: `Review ready: ${r.task.title}` });
  }
  for (const r of a.audits) {
    items.push({ id: `audit:${r.task.id}`, label: `Audit: ${r.task.title}` });
  }
  return items;
}

export function renderNotifications(
  root: HTMLElement,
  entry: DaemonEntry,
  onBack: () => void,
  onFeed: () => void,
  onBoard: () => void,
  onSettings: () => void,
): () => void {
  clear(root);

  const dot = el("span", { class: "status-dot" });
  const banner = el("div", {});
  const summary = el("div", { class: "notif-panel" }, [el("div", { class: "notif-row meta" }, ["Loading…"])]);
  const pushSlot = el("div", { class: "notif-push-page" });

  root.append(
    el("header", { class: "topbar" }, [
      backButton(onBack),
      el("h1", {}, [dot, `${entry.name} · Notifications`]),
    ]),
    banner,
    summary,
    pushSlot,
    bottomNav({ active: "feed", onFeed, onBoard, onSettings }),
  );

  let latestAttention: Attention | null = null;

  const renderSummary = () => {
    summary.replaceChildren();
    if (!latestAttention) {
      summary.append(el("div", { class: "notif-row meta" }, ["Loading…"]));
      return;
    }
    const a = latestAttention;
    const decs = a.decisions.length;
    const pls = a.plans.filter((p) => p.status === "proposed").length;
    const revs = a.reviews.length;
    const auds = a.audits.length;
    if (decs + pls + revs + auds === 0) {
      summary.append(el("div", { class: "notif-row meta" }, ["Nothing pending right now."]));
      return;
    }
    if (decs) summary.append(el("div", { class: "notif-row" }, [`${decs} decision${decs > 1 ? "s" : ""} pending`]));
    if (pls) summary.append(el("div", { class: "notif-row" }, [`${pls} plan${pls > 1 ? "s" : ""} awaiting approval`]));
    if (revs) summary.append(el("div", { class: "notif-row" }, [`${revs} review${revs > 1 ? "s" : ""} ready`]));
    if (auds) summary.append(el("div", { class: "notif-row" }, [`${auds} audit${auds > 1 ? "s" : ""} to check`]));
  };

  let conn: Connection | null = null;
  let stopped = false;
  let reconnectDelay = 1000;

  const setOnline = (online: boolean) => {
    dot.className = `status-dot ${online ? "online" : "offline"}`;
  };

  const refetch = async () => {
    if (!conn) return;
    try {
      const next = await conn.api<Attention>("GET", "/api/attention");
      void notifyNewAttention(entry.name, latestAttention, next);
      latestAttention = next;
      renderSummary();
      banner.replaceChildren();
    } catch (e) {
      banner.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
    }
  };

  const connect = async () => {
    if (stopped) return;
    banner.replaceChildren();
    const c = new Connection(entry, {
      onEvent: () => void refetch(),
      // Race fix: the daemon's push key arrives after connect — re-render the
      // push controls the moment it does so Enable can't be tapped key-less.
      onVapid: () => void renderPushControls(pushSlot, c),
      onClose: () => {
        setOnline(false);
        if (stopped) return;
        banner.replaceChildren(el("div", { class: "notice" }, ["Connection lost — reconnecting…"]));
        window.setTimeout(() => void connect(), reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
      },
    });
    try {
      await c.connect();
      conn = c;
      reconnectDelay = 1000;
      setOnline(true);
      void syncPush(c);
      void renderPushControls(pushSlot, c);
      await refetch();
    } catch (e) {
      setOnline(false);
      banner.replaceChildren(
        el("div", { class: "error-banner" }, [(e as Error).message]),
        el("div", { class: "actions" }, [el("button", { onclick: () => void connect() }, ["Retry"])]),
      );
    }
  };
  void connect();

  return () => {
    stopped = true;
    conn?.close();
  };
}
